import React from 'react';
import { Box, Typography, Chip, ButtonBase } from '@mui/material';
import {
  Phonelink,
  AdsClick,
  Insights,
  CallToAction,
  LocalPhone,
  PhoneInTalk,
  ChatBubbleOutline,
  SmartToy,
  RecordVoiceOver,
  Cancel,
  CheckCircle,
  CompareArrows
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { usePricing } from '../../contexts/PricingContext';
import {
  formatDisplayPrice,
} from '../../utils/currencyUtils';
import { formatSnapshotPrice } from '../../utils/pricingItemNormalizer';
import { getProductFromSnapshot, formatProductMetadata, getPhoneNumbersFromSnapshot, getVoiceTrafficFromSnapshot } from '../../utils/snapshotUtils';

// Icon mapping
const productIcons = {
  'business-phone': Phonelink,
  'fmc': Phonelink,
  'call-tracking': AdsClick,
  'ai-conversation': Insights,
  'callback': CallToAction,
  'phone-numbers': LocalPhone,
  'voice-traffic': PhoneInTalk,
  'omnichannel-chat': ChatBubbleOutline,
  'eniq-ai': SmartToy,
  'voicebot': RecordVoiceOver
};

/**
 * ProductDisplay component - unified product display for OfferPage, ComparisonPage, and OffersHistoryPage
 * @param {Object} props
 * @param {Object} props.snapshot - Pricing snapshot containing product data
 * @param {string} props.productType - Product type to display
 * @param {string} props.label - Product label (optional, will use translation if not provided)
 * @param {boolean} props.showNotIncluded - Show "Not included" for inactive products (default: true)
 * @param {string} props.variant - Display variant: 'default', 'compact', 'minimal' (default: 'default')
 * @param {string} props.context - Display context: 'offer', 'comparison', 'history' (defaults to 'comparison')
 * @param {function} props.onCompareClick - Optional callback for "Compare tiers" click (for business-phone/fmc)
 * @param {Object} props.sx - Additional styles
 */
const ProductDisplay = ({
  snapshot,
  productType,
  label,
  showNotIncluded = true,
  variant = 'default',
  context = 'comparison',
  onCompareClick,
  sx = {}
}) => {
  const { t } = useTranslation();
  
  // Determine if we should use compact display mode
  const isCompact = context === 'comparison';
  
  // Get user preferences and pricing data for currency conversion
  // Always call hooks unconditionally, then handle undefined values
  const { displayCurrency = 'USD' } = useUserPreferences() || {};
  const { currencyRates = {} } = usePricing() || {};
  
  // Get region from snapshot, not from user preferences context
  const snapshotRegion = snapshot?.region;
  const compactIconSize = 18;
  const compactLabelFontSize = '0.875rem';
  const compactMetaFontSize = '0.75rem';
  const compactPriceFontSize = '0.875rem';

  // Use the unified snapshot formatter for phone-numbers items. Modern
  // snapshots carry priceCurrency on each item (data is already in region
  // pricing currency); legacy snapshots without metadata fall back to the
  // pre-normalization USD→EUR conversion path inside formatSnapshotPrice.
  const formatPhoneNumberItem = (phoneItem) => formatSnapshotPrice(phoneItem, {
    region: snapshotRegion,
    displayCurrency,
    rates: currencyRates,
    productType: 'phone-numbers',
  });
  
  // Special handling for phone numbers (multiple entries)
  if (productType === 'phone-numbers') {
    const phoneNumbers = getPhoneNumbersFromSnapshot(snapshot);
    const isIncluded = phoneNumbers.length > 0;
    
    // Don't render if not included and showNotIncluded is false
    if (!isIncluded && !showNotIncluded) {
      return null;
    }
    
    // Get icon
    const Icon = productIcons[productType] || Phonelink;
    
    // Get label with fallback to translation
    const displayLabel = label || getProductLabel(productType, t);
    
    // If no phone numbers, show "Not included"
    if (!isIncluded) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          py: variant === 'compact' ? 1 : 1.5, 
          minHeight: variant === 'compact' ? 'auto' : '70px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          opacity: 0.5,
          ...sx
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: variant === 'compact' ? 1 : 1.5 }}>
            <Icon sx={{ 
              color: '#bdbdbd', 
              fontSize: variant === 'compact' ? compactIconSize : 24
            }} />
            <Typography variant="body2" sx={{ 
              fontSize: variant === 'compact' ? '0.875rem' : 'inherit',
              fontWeight: variant === 'compact' ? 400 : 500, 
              color: 'text.secondary' 
            }}>
              {displayLabel}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary', 
            fontStyle: 'italic',
            fontSize: variant === 'compact' ? '0.75rem' : 'inherit'
          }}>
            {t('offers.notIncluded', 'Not included')}
          </Typography>
        </Box>
      );
    }

    // Multiple phone numbers - render each separately
    return (
      <Box sx={sx}>
        {phoneNumbers.map((phoneNumber, index) => (
          <Box key={index} sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            py: variant === 'compact' ? 1 : 1.5, 
            minHeight: variant === 'compact' ? 'auto' : '70px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: variant === 'compact' ? 1 : 1.5 }}>
              <Icon sx={{ 
                color: variant === 'default' ? '#00C853' : '#757575', 
                fontSize: variant === 'compact' ? compactIconSize : 24
              }} />
              <Typography variant="body1" sx={{ 
                fontSize: variant === 'compact' ? compactLabelFontSize : '1.1rem',
                fontWeight: 500, 
                color: 'text.primary' 
              }}>
                {phoneNumber.name || phoneNumber.label}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {variant === 'compact' && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: compactMetaFontSize }}>
                  {phoneNumber.count} {t('calculator.phoneNumbersCount', 'numbers')}
                </Typography>
              )}
              {variant === 'default' && (
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.9rem' }}>
                  {phoneNumber.count} {t('calculator.phoneNumbersCount', 'numbers')}
                </Typography>
              )}
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                fontSize: variant === 'compact' ? compactPriceFontSize : '1.1rem'
              }}>
                {formatPhoneNumberItem(phoneNumber)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // Special handling for voice traffic (potentially multiple entries)
  if (productType === 'voice-traffic') {
    const voiceTrafficEntries = getVoiceTrafficFromSnapshot(snapshot);
    const isIncluded = voiceTrafficEntries.length > 0;
    
    // Don't render if not included and showNotIncluded is false
    if (!isIncluded && !showNotIncluded) {
      return null;
    }
    
    // Get icon
    const Icon = productIcons[productType] || PhoneInTalk;
    
    // Get label with fallback to translation
    const displayLabel = label || getProductLabel(productType, t);
    
    // If no voice traffic entries, show "Not included"
    if (!isIncluded) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          py: variant === 'compact' ? 1 : 1.5, 
          minHeight: variant === 'compact' ? 'auto' : '70px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          opacity: 0.5,
          ...sx
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: variant === 'compact' ? 1 : 1.5 }}>
            <Icon sx={{ 
              color: '#bdbdbd', 
              fontSize: variant === 'compact' ? compactIconSize : 24
            }} />
            <Typography variant="body2" sx={{ 
              fontSize: variant === 'compact' ? '0.875rem' : 'inherit',
              fontWeight: variant === 'compact' ? 400 : 500, 
              color: 'text.secondary' 
            }}>
              {displayLabel}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary', 
            fontStyle: 'italic',
            fontSize: variant === 'compact' ? '0.75rem' : 'inherit'
          }}>
            {t('offers.notIncluded', 'Not included')}
          </Typography>
        </Box>
      );
    }

    // Multiple voice traffic entries - render each separately or combined based on mode
    if (voiceTrafficEntries.length === 1 && voiceTrafficEntries[0].useCustomAmount) {
      // Custom amount mode - single entry
      const voiceEntry = voiceTrafficEntries[0];
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          py: variant === 'compact' ? 1 : 1.5, 
          minHeight: variant === 'compact' ? 'auto' : '70px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          ...sx
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: variant === 'compact' ? 1 : 1.5 }}>
            <Icon sx={{ 
              color: variant === 'default' ? '#00C853' : '#757575', 
              fontSize: variant === 'compact' ? compactIconSize : 24
            }} />
            <Typography variant="body1" sx={{ 
              fontSize: variant === 'compact' ? compactLabelFontSize : '1.1rem',
              fontWeight: 500, 
              color: 'text.primary' 
            }}>
              {displayLabel}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: variant === 'compact' ? compactMetaFontSize : '0.9rem' }}>
              {formatProductMetadata(voiceEntry, productType, t, isCompact)}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: variant === 'compact' ? compactPriceFontSize : '1.1rem' }}>
              {formatDisplayPrice(voiceEntry.price, displayCurrency, currencyRates, snapshotRegion)}
            </Typography>
          </Box>
        </Box>
      );
    } else {
      // Package mode - multiple countries, render each separately
      return (
        <Box sx={sx}>
          {voiceTrafficEntries.map((voiceEntry, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              py: variant === 'compact' ? 1 : 1.5, 
              minHeight: variant === 'compact' ? 'auto' : '70px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: variant === 'compact' ? 1 : 1.5 }}>
                <Icon sx={{ 
                  color: variant === 'default' ? '#00C853' : '#757575', 
                  fontSize: variant === 'compact' ? compactIconSize : 24
                }} />
                <Typography variant="body1" sx={{ 
                  fontSize: variant === 'compact' ? compactLabelFontSize : '1.1rem',
                  fontWeight: 500, 
                  color: 'text.primary' 
                }}>
                  {(() => {
                    // Show country-specific labels - ComparisonPage shows codes, OfferPage shows full names
                    if (context === 'comparison') {
                      // Try to extract country code from label if not directly available
                      let countryCode = voiceEntry.countryCode;
                      if (!countryCode && voiceEntry.label) {
                        // Extract from labels like "Пакет хвилин Україна" -> "UA"
                        if (voiceEntry.label.includes('Україна') || voiceEntry.label.includes('Ukraine')) {
                          countryCode = 'UA';
                        } else if (voiceEntry.label.includes('Казахстан') || voiceEntry.label.includes('Kazakhstan')) {
                          countryCode = 'KZ';
                        } else if (voiceEntry.label.includes('Польща') || voiceEntry.label.includes('Poland')) {
                          countryCode = 'PL';
                        }
                      }
                      
                      if (countryCode) {
                        return `${displayLabel} ${countryCode.toUpperCase()}`;
                      }
                    }
                    
                    return voiceEntry.label || displayLabel;
                  })()}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                {/* Show minutes info for non-custom amount mode */}
                {!voiceEntry.useCustomAmount && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', fontSize: variant === 'compact' ? compactMetaFontSize : '0.9rem' }}>
                    {(() => {
                      if (context === 'comparison') {
                        // ComparisonPage: show actual package minutes + overage (if any)
                        const baseMinutes = voiceEntry.minutes || 0;
                        const overageMinutes = voiceEntry.overageMinutes || 0;

                        if (overageMinutes > 0) {
                          // Show package + overage
                          const totalMinutes = baseMinutes + overageMinutes;
                          return totalMinutes > 0 ?
                            `${totalMinutes.toLocaleString()} ${t('calculator.voiceTrafficMinutes', 'хвилин')}` : '';
                        } else {
                          // Show only package minutes
                          return baseMinutes > 0 ?
                            `${baseMinutes.toLocaleString()} ${t('calculator.voiceTrafficMinutes', 'хвилин')}` : '';
                        }
                      } else {
                        // OfferPage: show detailed metadata using formatProductMetadata
                        return formatProductMetadata(voiceEntry, productType, t, isCompact);
                      }
                    })()}
                  </Typography>
                )}
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: variant === 'compact' ? compactPriceFontSize : '1.1rem' }}>
                  {formatDisplayPrice(voiceEntry.price, displayCurrency, currencyRates, snapshotRegion)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      );
    }
  }
  
  // Regular product handling (non-phone-numbers, non-voice-traffic)
  const productData = getProductFromSnapshot(snapshot, productType);
  const isIncluded = !!productData;

  // Get icon
  const Icon = productIcons[productType] || Phonelink;

  // Get label with fallback to translation (pass productData for tier info)
  const displayLabel = label || getProductLabel(productType, t, productData);
  
  // Don't render if not included and showNotIncluded is false
  if (!isIncluded && !showNotIncluded) {
    return null;
  }
  
  // Minimal variant - just icon and price
  if (variant === 'minimal') {
    if (!isIncluded) return null;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
        <Icon sx={{ fontSize: 16, color: '#757575' }} />
        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
          {displayLabel} {formatProductMetadata(productData, productType, t, isCompact)}
        </Typography>
      </Box>
    );
  }
  
  // Compact variant - smaller spacing and fonts
  if (variant === 'compact') {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        opacity: isIncluded ? 1 : 0.5,
        ...sx
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{
            color: isIncluded ? '#757575' : '#bdbdbd',
            fontSize: 18
          }} />
          {/* Clickable label for business-phone and fmc when onCompareClick is provided */}
          {isIncluded && onCompareClick && (productType === 'business-phone' || productType === 'fmc') ? (
            <Typography
              variant="body2"
              onClick={() => onCompareClick(productType, productData?.tier, snapshot?.region)}
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'text.primary',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#00C853'
                }
              }}
            >
              {displayLabel}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{
              fontSize: '0.875rem',
              fontWeight: isIncluded ? 500 : 400,
              color: isIncluded ? 'text.primary' : 'text.secondary'
            }}>
              {displayLabel}
            </Typography>
          )}
        </Box>
        {isIncluded ? (
          <Box sx={{ textAlign: 'right' }}>
            {/* Show "Included" tag for $0 products (included in Pro/Corporate) */}
            {productData.price === 0 ? (
              <Chip
                icon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
                label={t('offers.included', 'Included')}
                size="small"
                sx={{
                  backgroundColor: 'rgba(0, 200, 83, 0.12)',
                  color: '#00C853',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: '22px',
                  '& .MuiChip-icon': {
                    color: '#00C853'
                  }
                }}
              />
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.75rem' }}>
                  {formatProductMetadata(productData, productType, t, isCompact)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  {formatDisplayPrice(productData.price, displayCurrency, currencyRates, snapshotRegion)}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            fontSize: '0.75rem'
          }}>
            {t('offers.notIncluded', 'Not included')}
          </Typography>
        )}
      </Box>
    );
  }

  // Default variant - full size
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1.5,
      minHeight: '70px',
      borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      opacity: isIncluded ? 1 : 0.5,
      ...sx
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Icon sx={{
          color: isIncluded ? '#00C853' : '#bdbdbd',
          fontSize: 24
        }} />
        {/* Clickable label for business-phone and fmc on comparison context */}
        {isIncluded && onCompareClick && context === 'comparison' && (productType === 'business-phone' || productType === 'fmc') ? (
          <Typography
            variant="body1"
            onClick={() => onCompareClick(productType, productData?.tier, snapshot?.region)}
            sx={{
              fontWeight: 500,
              fontSize: '1.1rem',
              color: 'text.primary',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
                color: '#00C853'
              }
            }}
          >
            {displayLabel}
          </Typography>
        ) : (
          <Typography variant="body1" sx={{
            fontWeight: 500,
            fontSize: '1.1rem',
            color: isIncluded ? 'text.primary' : 'text.secondary'
          }}>
            {displayLabel}
          </Typography>
        )}
        {/* Compare button for business-phone and fmc on offer context */}
        {isIncluded && onCompareClick && context === 'offer' && (productType === 'business-phone' || productType === 'fmc') && (
          <ButtonBase
            onClick={() => onCompareClick(productType, productData?.tier, snapshot?.region)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#666',
              ml: 1,
              '&:hover': { color: '#00C853' }
            }}
          >
            <CompareArrows sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.875rem' }}>
              {t('calculator.compareFeatures', 'Порівняти функції')}
            </Typography>
          </ButtonBase>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
        {isIncluded ? (
          <Box sx={{ textAlign: 'right' }}>
            {/* Show "Included" tag for $0 products (included in Pro/Corporate) */}
            {productData.price === 0 ? (
              <Chip
                icon={<CheckCircle sx={{ fontSize: '16px !important' }} />}
                label={t('offers.included', 'Included')}
                size="small"
                sx={{
                  backgroundColor: 'rgba(0, 200, 83, 0.12)',
                  color: '#00C853',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: '26px',
                  '& .MuiChip-icon': {
                    color: '#00C853'
                  }
                }}
              />
            ) : (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.9rem' }}>
                  {formatProductMetadata(productData, productType, t, isCompact)}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.1rem' }}>
                  {formatDisplayPrice(productData.price, displayCurrency, currencyRates, snapshotRegion)}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
              fontWeight: 400
            }}>
              {t('offers.notIncluded', 'Not included')}
            </Typography>
            <Cancel sx={{
              color: '#f44336',
              fontSize: 18
            }} />
          </>
        )}
      </Box>
    </Box>
  );
};

// Helper function to get product label
const getProductLabel = (type, t, productData = null) => {
  const labels = {
    'business-phone': t('calculator.businessPhoneLabel', 'Business Phone'),
    'fmc': t('calculator.fmcLabel', 'FMC'),
    'call-tracking': t('calculator.callTrackingLabel', 'Call Tracking'),
    'ai-conversation': t('calculator.aiConversationLabel', 'AI Conversation Intelligence'),
    'callback': t('calculator.callbackLabel', 'Callback Widget'),
    'voice-traffic': t('calculator.voiceTrafficLabel', 'Voice Traffic'),
    'phone-numbers': t('calculator.phoneNumbers', 'Phone Numbers'),
    'omnichannel-chat': t('calculator.omnichannelChat', 'Omnichannel Chat'),
    'eniq-ai': t('calculator.eniqAiLabel', 'Eniq AI'),
    'voicebot': t('calculator.voicebotLabel', 'Voice bot')
  };

  let baseLabel = labels[type] || type;

  // For Business Phone and FMC, append tier to the label if available
  if ((type === 'business-phone' || type === 'fmc') && productData?.tier) {
    const tierLabel = productData.tier.charAt(0).toUpperCase() + productData.tier.slice(1);
    baseLabel = `${baseLabel} ${tierLabel}`;
  }

  return baseLabel;
};

export default ProductDisplay;
